import React, { useContext, useState } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import { Card, UtilsBlock, ButtonsBlock, Button, TextLarge, Checkbox, Title4 } from '@hz-design-system/web-ui';
import EnvironmentContext from '../../../contexts/EnvironmentContext';
import { ToastContainer } from 'react-toastify';
import useToastNotification from '../../../hooks/useToastNotification';
import type { TToastNotificationProps } from '../../../components/Toast/ToastNotification';
import submitSocialCreateAccount from '../repositories/submitSocialCreateAccount';
import { trackGAEvent } from '../../../utils/gaTracking';
import {
  GOOGLE,
  USER_EXISTS,
  USER_BLOCKED,
  GA_EVENTS,
  CONFIRMATION_REQUIRED,
  TWO_FACTOR_AUTH_SETUP_REQUIRED,
  TWO_FACTOR_AUTH_NEEDED,
  FORCE_PASSWORD_RESET,
  CREDENTIALS_MUST_BE_UPDATED,
  VERIFICATION_REQUESTS_BLOCKED,
  VERIFICATION_CODE_BLOCKED,
  FLOW,
} from '../../../../../common/constants';
import { useRecaptcha, useRecaptchaHandler } from '../../../hooks/useRecaptcha';
import Classes from './CreateAccountComponent.module.scss';
import { ClientError } from '@ecg-marktplaats/aurora-api-core';

const SocialCreateAccountComponent = () => {
  const { t } = useI18nContext();
  const { toast } = useToastNotification<TToastNotificationProps>();
  const {
    xsrfToken,
    threatMetrix,
    emailNewsletterOptin,
    recaptchaSiteKey,
    recaptchaScriptUrl,
    socialAccessToken,
    successUrl,
  } = useContext(EnvironmentContext);

  useRecaptcha(recaptchaScriptUrl, recaptchaSiteKey);

  const [isChecked, setIsChecked] = useState(emailNewsletterOptin || false);
  const [showEmailToggle, setShowEmailToggle] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const resetPwdUrl = '/identity/v2/reset-password?forcePwdReset=true';

  const handleSubmitSocialCreateAccount = async () => {
    setIsLoading(true);
    try {
      trackGAEvent(GA_EVENTS.SocialUserRegistrationBegin);
      const { redirectUrl = '/' } = await submitSocialCreateAccount({
        xsrfToken,
        emailNewsletterOptin,
        threatMetrix,
        socialNetwork: GOOGLE,
        accessToken: socialAccessToken,
        recaptchaToken,
      });
      trackGAEvent(GA_EVENTS.SocialUserRegistrationSuccess);
      window.location.assign(redirectUrl);
    } catch (error) {
      trackGAEvent(GA_EVENTS.SocialUserRegistrationFail);
      const errorMessage = (error as ClientError).message;
      const errorCode = (error as ClientError).code;
      const errorDetails = (error as ClientError).details;

      const { verification, verificationSetup, confirmation } =
        (errorDetails as { verification?: any; verificationSetup?: any; confirmation?: any }) ?? {};

      let errorCheck = errorMessage;
      if (typeof errorCode === 'string') {
        errorCheck = errorCode;
      }
      switch (errorCheck) {
        case TWO_FACTOR_AUTH_SETUP_REQUIRED:
          window.location.assign(
            `/identity/v2/two-factor-auth-setup?key=${verificationSetup.key}&success_url=${successUrl}&flow=${FLOW.SOCIAL_CREATE_ACCOUNT}`,
          );
          break;
        case TWO_FACTOR_AUTH_NEEDED:
          window.location.assign(
            `/identity/v2/two-factor-auth-setup/challenge?key=${verification.id}&success_url=${successUrl}&flow=${FLOW.SOCIAL_CREATE_ACCOUNT}`,
          );
          break;
        case FORCE_PASSWORD_RESET:
        case CREDENTIALS_MUST_BE_UPDATED:
          window.location.assign(resetPwdUrl);
          break;
        case VERIFICATION_REQUESTS_BLOCKED:
        case VERIFICATION_CODE_BLOCKED:
          void toast({ type: 'error', description: t('pages.login.validation.verification_requests_blocked') });
          break;
        case CONFIRMATION_REQUIRED: {
          const { required, magicToken } = confirmation;
          const twoFaConfirmation = required.find(({ type }: { type: string }) => type.toLowerCase() === 'phone');
          window.location.assign(
            `/identity/v2/two-factor-auth-setup?magic_token=${magicToken}&key=${twoFaConfirmation.key}&type=phone&flow=${FLOW.SOCIAL_CREATE_ACCOUNT}`,
          );
          break;
        }
        case USER_EXISTS:
          void toast({
            type: 'error',
            description: t('pages.create_account.validation.email_exists'),
          });
          break;
        case USER_BLOCKED:
          void toast({
            type: 'error',
            description: t('pages.create_account.validation.user_blocked'),
          });
          break;
        default:
          void toast({
            type: 'error',
            description: t('pages.create_account.validation.unknown_server_error'),
          });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = useRecaptchaHandler({
    recaptchaSiteKey: recaptchaSiteKey || '',
    onSuccess: (token: string) => {
      setRecaptchaToken(token);
      void handleSubmitSocialCreateAccount();
    },
    onError: () => {
      void toast({
        type: 'error',
        description: t('pages.create_account.validation.no_captcha_been_entered'),
      });
    },
  });

  return (
    <div className={Classes.root}>
      <UtilsBlock pb="s">
        <Title4>{t('pages.create_account.social_login_success.title')}</Title4>
      </UtilsBlock>
      <Card padding="l" style={{ textAlign: 'left' }}>
        <UtilsBlock pl="l" pr="l" pb="s" pt="xxs">
          <TextLarge>{t('pages.create_account.social_login_success.sub_title_gdpr')}</TextLarge>
        </UtilsBlock>
        <UtilsBlock pl="l" pr="l" pb="s" pt="xxs">
          <div
            data-testid="toggle-opt-in"
            onClick={() => setShowEmailToggle(!showEmailToggle)}
            onKeyDown={() => setShowEmailToggle(!showEmailToggle)}
            /* eslint-disable-next-line react/no-danger */
            dangerouslySetInnerHTML={{ __html: t('pages.create_account.gdpr_subscription_disclaimer') }}
          />
          {showEmailToggle && (
            <UtilsBlock pb="s" pt="xxs">
              <Checkbox
                value="1234"
                id="checkbox-1234"
                isChecked={isChecked}
                labelText={t('pages.create_account.gdrp_subscription_checkbox_label')}
                onChange={() => setIsChecked(!isChecked)}
              />
            </UtilsBlock>
          )}
        </UtilsBlock>
        <UtilsBlock pl="l" pr="l" pb="s" pt="xxs">
          <div
            /* eslint-disable-next-line react/no-danger */
            dangerouslySetInnerHTML={{ __html: t('pages.create_account.social_login_success.terms_and_conditions') }}
          />
        </UtilsBlock>
        <ButtonsBlock>
          <Button.Primary disabled={isLoading} onClick={handleSubmit}>
            {t('pages.create_account.social_login_success.submit_button')}
          </Button.Primary>
        </ButtonsBlock>
      </Card>
      <ToastContainer />
    </div>
  );
};

export default SocialCreateAccountComponent;
